// Step 7 — Cash Flow view figures (CAXeng.md §6.4 and caxdemo_ui.md §1.4 "Cash-flow numbers").
import type { PartyIndex, PartyScenario } from '@divorceos/schema';
import { receivedBy } from './guideline.js';
import type { NCase } from './normalize.js';
import { ANNUAL, type ScenarioSpec, type TaxContext, type TaxOutcome } from './taxes.js';

export interface ScenarioInputs {
  spec: ScenarioSpec;
  taxes: [TaxOutcome, TaxOutcome];
  /** p0-signed total support (CS + SS + add-ons). */
  support: number;
}

export interface WithholdingResult { allowances: number; extraNeeded: boolean; netWagePaycheck: number }

/**
 * §6.4 withholding / net wage paycheck as the original computes them: `Calculate_WH_Allowances` returns 0 under
 * the 2020+ Form W-4 and the engine's search only ever lowers the count, so the allowances are 0; the federal and
 * state withholding are the annual Pub 15-T / DE 44 amounts at 0 allowances on the MEDICARE wages (wages − pre-tax
 * health, before 401(k)); net wage paycheck = (taxable wages (after 401(k) and pre-tax health) − (federal WH + FICA
 * + state WH + SDI)) / 12, floored at 0. "Extra withholding needed" flags a federal WH below the income tax.
 */
export function withholding(ctx: TaxContext, p: PartyIndex, t: TaxOutcome): WithholdingResult {
  const nc = ctx.nc;
  const np = nc.parties[p];
  const taxableMonthly = np.in.wages - np.in.healthInsurance.wageDeductionPreTax - np.in.employee401k;
  const medicareMonthly = np.in.wages - np.in.healthInsurance.wageDeductionPreTax;
  const taxable = Math.max(0, taxableMonthly) * ANNUAL;
  const medicare = Math.max(0, medicareMonthly) * ANNUAL;
  const target = Math.max(0, t.taxes.federalIncome * ANNUAL);
  const fallback = (): WithholdingResult => ({
    allowances: 0, extraNeeded: false,
    netWagePaycheck: Math.max(0, taxableMonthly - t.taxes.federalIncome - t.taxes.stateIncome - t.taxes.fica - t.taxes.sdi),
  });
  if (medicare <= 0) return { ...fallback(), netWagePaycheck: 0 };
  try {
    const year = nc.year as 2022 | 2023 | 2024 | 2025;
    const fed = Math.max(0, ctx.provider.federalWithholding(year, np.irs, medicare, { dependentsCredit: 0, deductions: 0, otherIncome: 0 }));
    const st = nc.c.settings.taxState[p] === 5 ? Math.max(0, ctx.provider.stateWithholding(year, np.stateIrs, medicare, 0)) : 0;
    const net = Math.max(0, (taxable - fed - st) / ANNUAL - t.taxes.fica - t.taxes.sdi);
    return { allowances: 0, extraNeeded: fed < target, netWagePaycheck: net };
  } catch {
    return fallback();
  }
}

/** Cash Flow column for party p in one scenario. `base` = no-support taxes, `guidelineNs` = the guideline column's NS. */
export function partyScenario(
  ctx: TaxContext, p: PartyIndex, s: ScenarioInputs, base: [TaxOutcome, TaxOutcome], formulaNet: number,
  guidelineNs: number | null, combined: number, combinedGuideline: number | null,
): PartyScenario {
  const nc: NCase = ctx.nc;
  const t = s.taxes[p];
  const saving = base[p].incomeTax - t.incomeTax;
  const netSpendable = formulaNet + saving + receivedBy(s.support, p);
  const paymentCostBenefit = saving + receivedBy(s.support, p);
  const changeFromGuideline = guidelineNs === null ? 0 : netSpendable - guidelineNs;
  const combinedChange = combinedGuideline === null ? 0 : combined - combinedGuideline;
  const percentOfSaving = Math.abs(combinedChange) > 0.005 ? (changeFromGuideline / combinedChange) * 100 : 0;
  // Dependent exemption value: tax with the party's dependents removed − tax as filed.
  let dependentExemptionValue = 0;
  const ex = nc.parties[p].exemptions;
  if (ex.dependent > 0 || ex.ctc > 0 || ex.eic > 0 || ex.ccc > 0 || ex.otherDependents > 0 || ex.under6 > 0) {
    const noDeps = ctx.scenario({ ...s.spec, zeroDependents: p });
    dependentExemptionValue = noDeps[p].incomeTax - t.incomeTax;
  }
  const wh = withholding(ctx, p, t);
  const f = t.federal.marginalRate, st = t.state.marginalRate;
  return {
    taxes: t.taxes,
    federal: t.federal,
    state: t.state,
    netSpendable,
    paymentCostBenefit,
    changeFromGuideline,
    percentOfCombinedSpendable: Math.abs(combined) > 0.005 ? (netSpendable / combined) * 100 : 0,
    percentOfSavingOverGuideline: percentOfSaving,
    dependentExemptionValue,
    withholdingAllowances: wh.allowances,
    extraWithholdingNeeded: wh.extraNeeded,
    netWagePaycheck: wh.netWagePaycheck,
    federalMarginal: f,
    stateMarginal: st,
    combinedMarginal: f + st - f * st,
  };
}
